import { captureException } from '@sentry/react-router'
import { useEffect } from 'react'
import {
	isRouteErrorResponse,
	Link,
	useLocation,
	useRouteError,
} from 'react-router'
import { MainLayout } from '#app/components/main-layout.tsx'

export default function MarketingLayout() {
	return <MainLayout />
}

/**
 * Without a boundary here, anything thrown from a marketing route bubbles to
 * the root one, which replaces the entire document: a mistyped blog slug
 * rendered as bare "404 Not found" on black, with no header, no footer and no
 * way back. Blog URLs are exactly the ones that get shared and go stale.
 *
 * The boundary has to render `MainLayout` itself. React Router swaps a layout
 * route's element for its error element, so the chrome is only on screen if
 * the boundary puts it there - hence `MainLayout`'s optional `children`.
 */
export function ErrorBoundary() {
	const error = useRouteError()
	const { pathname } = useLocation()
	const isResponse = isRouteErrorResponse(error)
	const status = isResponse ? error.status : 500
	const notFound = status === 404

	useEffect(() => {
		// Route responses are intentional; only genuine faults are worth an event.
		if (isResponse) return
		captureException(error)
	}, [error, isResponse])

	const heading = notFound
		? ['Nothing at this ', 'path', '.']
		: ['This page failed to ', 'load', '.']

	const facts = [
		{ term: 'Status', value: String(status) },
		{ term: 'Requested', value: pathname },
		{ term: 'Reported', value: notFound ? 'not logged' : 'sent to engineers' },
	]

	return (
		<MainLayout>
			<title>{notFound ? 'Page not found - Syrcha' : 'Error - Syrcha'}</title>
			<section className="relative flex flex-1 items-center overflow-hidden bg-[var(--marketing-base)] py-24 sm:py-28">
				<div className="relative z-10 mx-auto grid w-full max-w-6xl gap-12 px-6 lg:grid-cols-[1.15fr_1fr] lg:items-end lg:gap-16 lg:px-8">
					<div>
						<p className="eyebrow text-[var(--marketing-faint)]">/ {status}</p>
						<h1
							className="mt-4 font-[family-name:var(--font-display)] text-[38px] leading-[1.04] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]"
							style={{ textWrap: 'balance' }}
						>
							{heading[0]}
							<span className="accent-underline">{heading[1]}</span>
							{heading[2]}
						</h1>
						<p
							className="mt-6 max-w-xl text-[16px] leading-relaxed text-[var(--marketing-gray)]"
							style={{ textWrap: 'pretty' }}
						>
							{notFound
								? 'The page moved, or the URL is mistyped. Nothing is broken. Pick a destination below.'
								: 'Something broke on our side, not yours. The error is logged with a trace we can follow. Try again, or tell us what you were doing.'}
						</p>

						<div className="mt-10 flex flex-wrap items-center gap-3">
							<Link to="/" className="btn-marketing">
								Back to home
								<span aria-hidden className="ml-2">
									→
								</span>
							</Link>
							<Link
								to={notFound ? '/blog' : '/support'}
								className="btn-marketing-outline"
							>
								{notFound ? 'Read the blog' : 'Contact support'}
							</Link>
						</div>
					</div>

					{/* The ruled fact ledger, applied to the failure itself: what was
					    asked for, what came back, and whether anyone was told. */}
					<dl className="border-t border-[var(--marketing-border)]">
						{facts.map((fact) => (
							<div
								key={fact.term}
								className="flex items-baseline justify-between gap-6 border-b border-[var(--marketing-border)] py-3.5"
							>
								<dt className="font-[family-name:var(--font-mono)] text-[12px] tracking-[0.14em] text-[var(--marketing-faint)] uppercase">
									{fact.term}
								</dt>
								<dd className="stat-number min-w-0 text-right text-[13px] break-all text-[var(--marketing-black)]">
									{fact.value}
								</dd>
							</div>
						))}
					</dl>
				</div>
			</section>
		</MainLayout>
	)
}
